import { FC, useEffect, useMemo, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import AsyncSelect from "react-select/async"
import ArrowDown from "@/shared/assets/images/ArrowDown.svg"
import { getDisplayName } from "@/entities/user"
import {
    GroupAccessSource,
    IVisibilityAclTableResponse,
    TargetType,
} from "@/shared/api/groups"
import { getUsers } from "@/shared/api/users"
import { fetchVisibilityAclTable } from "../model"
import { useAppDispatch, useAppSelector } from "@/shared/lib/redux"
import { selectEmployeesGroups, selectUsersGroups } from "@/entities/groups"
import { FormInput } from "@/shared/ui/form-input"
import { Table } from "@/shared/ui/table"
import { ColumnDef } from "@tanstack/react-table"

interface VisibilityAclTableProps {
    rows: IVisibilityAclTableResponse
    isLoading: boolean
}

type VisibilityAclFilters = {
    group_id: string
    viewer_target_type: "" | TargetType
    viewer_target_id: string
    group_access_source: "" | GroupAccessSource
}

const GROUP_ACCESS_SOURCE_LABELS: Record<GroupAccessSource, string> = {
    [GroupAccessSource.SELF]: "видит себя",
    [GroupAccessSource.DIRECT]: "прямой доступ",
    [GroupAccessSource.INHERITED]: "наследование от группы",
    [GroupAccessSource.FULL_ACCESS]: "полный доступ",
}

const VIEWER_TARGET_TYPE_OPTIONS = [
    { value: "", label: "Все" },
    { value: TargetType.GROUP, label: "Группа" },
    { value: TargetType.USER, label: "Пользователь" },
]

const loadUserOptions = (inputValue: string): Promise<{ value: string; label: string }[]> =>
    getUsers({ page: 1, limit: 50, search: inputValue || undefined, all: true })
        .then((response) =>
            response.data.items.map((user) => ({
                value: user.id,
                label: getDisplayName(user),
            }))
        )
        .catch((): { value: string; label: string }[] => [])

export const VisibilityAclTable: FC<VisibilityAclTableProps> = ({ rows, isLoading }) => {
    const dispatch = useAppDispatch()
    const usersGroups = useAppSelector(selectUsersGroups)
    const employeesGroups = useAppSelector(selectEmployeesGroups)
    const [selectedUserOption, setSelectedUserOption] = useState<{ value: string; label: string } | null>(null)

    const allGroups = useMemo(
        () => [...usersGroups, ...employeesGroups],
        [usersGroups, employeesGroups]
    )

    const groupOptions = useMemo(
        () => [{ value: "", label: "Все" }, ...allGroups.map((group) => ({ value: group.id, label: group.name }))],
        [allGroups]
    )

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        watch,
    } = useForm<any>({
        defaultValues: {
            group_id: "",
            viewer_target_type: "",
            viewer_target_id: "",
            group_access_source: "",
        },
    })

    const viewerTargetType: "" | TargetType = watch("viewer_target_type") || ""

    useEffect(() => {
        if (viewerTargetType !== TargetType.USER) {
            setSelectedUserOption(null)
        }
        setValue("viewer_target_id", "")
        if (viewerTargetType !== TargetType.GROUP) {
            setValue("group_access_source", "")
        }
    }, [viewerTargetType, setValue])

    const onSubmit = async (filters: VisibilityAclFilters) => {
        await dispatch(fetchVisibilityAclTable({
            ...(filters.group_id ? { group_id: filters.group_id } : {}),
            ...(filters.viewer_target_type ? { viewer_target_type: filters.viewer_target_type } : {}),
            ...(filters.viewer_target_id ? { viewer_target_id: filters.viewer_target_id } : {}),
            ...(filters.group_access_source ? { group_access_source: filters.group_access_source } : {}),
        }))
    }

    const onReset = async () => {
        reset({
            group_id: "",
            viewer_target_type: "",
            viewer_target_id: "",
            group_access_source: "",
        })
        setSelectedUserOption(null)
        await dispatch(fetchVisibilityAclTable(undefined))
    }

    const columns: ColumnDef<IVisibilityAclTableResponse[number], any>[] = [
        {
            id: "group",
            sortingFn: (rowA, rowB) => rowA.original.group.name.localeCompare(rowB.original.group.name),
            header: () => <div className="!text-start">Группа</div>,
            accessorFn: (row) => row.group.name,
            cell: (info) => <div>{info.getValue()}</div>,
        },
        {
            id: "viewer-groups",
            enableSorting: false,
            header: () => <div className="!text-start">Видна группам</div>,
            accessorFn: (row) => {
                const viewerGroups = row.viewers.filter((viewer) => viewer.target_type === "group")
                return viewerGroups.length
                    ? viewerGroups
                        .map((viewer) => {
                            const groupName = viewer.target_group?.name || "—"
                            const source = viewer.group_access_source
                                ? GROUP_ACCESS_SOURCE_LABELS[viewer.group_access_source]
                                : "—"
                            return `${groupName} (${source})`
                        })
                        .join(", ")
                    : "—"
            },
            cell: (info) => <div className="whitespace-normal break-words">{info.getValue()}</div>,
        },
        {
            id: "viewer-users",
            enableSorting: false,
            header: () => <div className="!text-start">Видна пользователям</div>,
            accessorFn: (row) => {
                const viewerUsers = row.viewers.filter((viewer) => viewer.target_type === "user")
                return viewerUsers.length
                    ? viewerUsers.map((viewer) => getDisplayName(viewer.target_user)).join(", ")
                    : "—"
            },
            cell: (info) => <div className="whitespace-normal break-words">{info.getValue()}</div>,
        },
    ]

    return (
        <div className="box">
            <div className="box_header font-medium">Таблица видимости групп</div>
            <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-12 gap-4 mb-4">
                <div className="col-span-12 lg:col-span-4">
                    <FormInput
                        label="Целевая группа"
                        id="group_id"
                        options={groupOptions}
                        control={control as any}
                        type="select"
                        required={false}
                        placeholder="Все"
                    />
                </div>
                <div className="col-span-12 lg:col-span-4">
                    <FormInput
                        label="Тип обьекта"
                        id="viewer_target_type"
                        options={VIEWER_TARGET_TYPE_OPTIONS}
                        control={control as any}
                        type="select"
                        required={false}
                        placeholder="Все"
                    />
                </div>
                <div className="col-span-12 lg:col-span-4">
                    {viewerTargetType === TargetType.USER ? (
                        <Controller
                            control={control}
                            name="viewer_target_id"
                            render={({ field }) => (
                                <div className="flex relative flex-col gap-[6px] pb-[8px] border-b-[1.5px] border-[#B9C9C6]">
                                    <label className="font-semibold uppercase text-base text-[#828282]">
                                        Пользователь
                                    </label>
                                    <AsyncSelect
                                        {...field}
                                        classNames={{
                                            control: () => "w-full !outline-0 !shadow-none !min-h-[auto] !border-0 !border-none !p-0 !outline-none",
                                            valueContainer: () => "!border-0 !outline-0 !p-0 text-color-dark text-lg",
                                            placeholder: () => "text-color-dark/70 text-base !m-0 text-color-dark",
                                            menu: () => "!left-[0]",
                                            input: () => "!m-0 !p-0",
                                            menuPortal: () => "!z-[9999]",
                                        }}
                                        loadOptions={loadUserOptions}
                                        value={selectedUserOption?.value === field.value ? selectedUserOption : null}
                                        onChange={(option) => {
                                            const selected = option as { value: string; label: string } | null
                                            field.onChange(selected?.value || "")
                                            setSelectedUserOption(selected)
                                        }}
                                        placeholder="Введите имя или телефон..."
                                        noOptionsMessage={() => "Начните вводить для поиска"}
                                        menuPosition="fixed"
                                        menuPortalTarget={document.body}
                                        components={{
                                            DropdownIndicator: () => <ArrowDown height={20} width={20} />,
                                            IndicatorSeparator: () => null,
                                        }}
                                    />
                                </div>
                            )}
                        />
                    ) : (
                        <FormInput
                            label="Группа"
                            id="viewer_target_id"
                            options={groupOptions}
                            control={control as any}
                            type="select"
                            required={false}
                            placeholder={viewerTargetType === TargetType.GROUP ? "Выберите группу" : "Все"}
                        />
                    )}
                </div>
                <div className="col-span-12 flex gap-3">
                    <button className="form_handle_button !w-auto px-6" type="submit" disabled={isLoading}>
                        Применить
                    </button>
                    <button
                        className="form_handle_button !w-auto px-6 !bg-gray-300 !text-color-dark"
                        type="button"
                        onClick={onReset}
                        disabled={isLoading}
                    >
                        Сбросить
                    </button>
                </div>
            </form>
            {!rows.length &&
                <div className="text-center py-8 text-gray-500">Нет данных</div>
            }
            {isLoading && !rows.length &&
                <div className="text-center py-8 text-gray-500">Загрузка...</div>
            }
            {Boolean(rows.length) &&
                <div className="mt-4">
                    <Table columns={columns} data={rows} />
                </div>
            }
        </div>
    )
}
